import React from "react";
import { Checkbox, CheckboxModes, Input } from "@trussle/external-components";
import Joi from "joi";
import { CTA } from "../CTA";
import { ErrorMessage } from "../ErrorMessage";
import "./styles.scss";
import { IMarketingConsents } from "../../types";
import { Spinner } from "../../../../Spinner";

const schema = Joi.object({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "email address",
      "string.email": "valid email address"
    }),
  emailMarketingConsent: Joi.string().optional()
});

type IEmailFormTemplateProps = {
  onValidSubmit: (email: string, consents: IMarketingConsents) => Promise<void>;
  ComponentToRenderOnSubmission: React.FC;
};

export const EmailFormTemplate: React.FC<IEmailFormTemplateProps> = ({
  onValidSubmit,
  ComponentToRenderOnSubmission
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<Joi.ValidationError | string | null>(null);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isConsentChecked, setIsConsentChecked] = React.useState(false);

  const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const inputs = new FormData(e.currentTarget);
    const inputEntries = Object.fromEntries(inputs.entries());

    const email = inputEntries.email.toString();
    const emailMarketingConsent = !!inputEntries.emailMarketingConsent;

    const { error } = schema.validate(inputEntries, { abortEarly: false });

    if (error) {
      setErrors(error);
    } else {
      setErrors(null);

      setIsLoading(true);

      try {
        await onValidSubmit(email, { email: emailMarketingConsent });

        setIsSubmitted(true);
      } catch (e) {
        if (e instanceof Error) {
          console.error(e);
          setErrors(e.message);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isSubmitted) {
    return <ComponentToRenderOnSubmission />;
  }

  return (
    <form className="heroff2-form" onSubmit={submitHandler}>
      <h2>Please provide your email address</h2>
      <Input type="email" label="What's your email address?" name="email" />
      <Checkbox
        mode={CheckboxModes.LightV4}
        checked={isConsentChecked}
        onChange={() => setIsConsentChecked(!isConsentChecked)}
        classOverrides={{
          container: "heroff2-email-form-checkbox-container",
          label: "heroff2-email-form-checkbox-label",
          box: "heroff2-email-form-checkbox-box"
        }}
        label="We can keep you up to date on the latest mortgage news and rates. You can unsubscribe at any time."
        name="emailMarketingConsent"
        id="email-consent-checkbox"
      />
      <ErrorMessage errors={errors} />
      <CTA>Get Started</CTA>
      <Spinner visible={isLoading} />
    </form>
  );
};
